import {
	type FetchConsumptionDataResult,
	useConsumptionData,
} from "@/api/consumption";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { formatCurrency } from "@/lib/format-for-view";
import { format, parseISO, startOfYear, subDays, subYears } from "date-fns";
import { fr } from "date-fns/locale";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";

interface MonthData {
	monthIndex: number;
	month: string;
	timestamp: string;
	cost: number;
	subscription: number;
	peak: number;
	offPeak: number;
	total: number;
}

interface YearlyData {
	currentYear: MonthData[];
	previousYear: MonthData[];
}

const AnnualExpenses = () => {
	const [currentYearData, setCurrentYearData] = useState<MonthData[]>([]);
	const [yearlyData, setYearlyData] = useState<YearlyData>({
		currentYear: [],
		previousYear: [],
	});

	const currentDate = useMemo(() => new Date(), []);
	const startOfCurrentYear = useMemo(
		() => startOfYear(currentDate),
		[currentDate],
	);
	const startOfPreviousYear = useMemo(
		() => startOfYear(subYears(currentDate, 1)),
		[currentDate],
	);
	const endOfPreviousYear = useMemo(
		() => subDays(startOfCurrentYear, 1),
		[startOfCurrentYear],
	);

	// Fetch current year consumption data
	const {
		data: currentYearConsumptionData,
		isLoading: isLoadingCurrentYear,
		error: errorCurrentYear,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: "MONTHLY",
		start_date: format(startOfPreviousYear, "yyyy-MM-dd"),
		end_date: format(subDays(currentDate, 2), "yyyy-MM-dd"),
	});

	const isLoading = isLoadingCurrentYear;
	const error = errorCurrentYear;

	// Transform API data to our format
	const transformData = useCallback(
		(apiData: FetchConsumptionDataResult["data"] | undefined) => {
			if (!apiData) return [];

			return apiData.map((item) => {
				const cost = item.cost || 0;
				const subscription = item.subscription_cost || 0;
				const peak = item.cost_hp || cost / 2; // Default to half if not available
				const offPeak = item.cost_hc || cost / 2; // Default to half if not available
				const date = new Date(item?.timestamp);

				return {
					month: format(date, "MMM", { locale: fr }),
					monthIndex: date.getMonth(),
					timestamp: item?.timestamp,
					cost,
					peak,
					offPeak,
					subscription,
					total: cost + subscription,
				};
			});
		},
		[],
	);

	// Process data when API responses are received
	useEffect(() => {
		const currentYearTransformed = transformData(
			currentYearConsumptionData?.data?.filter(
				(item) => parseISO(item.timestamp) >= startOfCurrentYear,
			),
		);
		const previousYearTransformed = transformData(
			currentYearConsumptionData?.data?.filter(
				(item) => parseISO(item.timestamp) <= endOfPreviousYear,
			),
		);
		setCurrentYearData(currentYearTransformed);
		setYearlyData({
			currentYear: currentYearTransformed,
			previousYear: previousYearTransformed,
		});
	}, [
		currentYearConsumptionData?.data,
		endOfPreviousYear,
		startOfCurrentYear,
		transformData,
	]);

	// Calculate percentage change from previous year for the same months
	const calculatePercentageChange = () => {
		const { currentYear, previousYear } = yearlyData;

		if (!currentYear.length || !previousYear.length) return null;

		// Get current month (0-11)
		const currentMonth = currentDate.getMonth();

		// Filter months that appear in both current and previous year data up to current month
		const availableMonthsInCurrent = currentYear
			.filter((item) => item.monthIndex <= currentMonth)
			.map((item) => item.monthIndex);

		const availableMonthsInPrevious = previousYear
			.filter((item) => item.monthIndex <= currentMonth)
			.map((item) => item.monthIndex);

		// Find common months between the two datasets
		const commonMonths = availableMonthsInCurrent.filter((month) =>
			availableMonthsInPrevious.includes(month),
		);

		// If no common months, we can't compare
		if (commonMonths.length === 0) return null;

		// Sum totals for common months in the current year
		const currentYearTotal = currentYear.reduce((sum, item) => {
			if (commonMonths.includes(item.monthIndex)) {
				return sum + item.total;
			}
			return sum;
		}, 0);

		// Sum totals for common months in the previous year
		const previousYearTotal = previousYear.reduce((sum, item) => {
			if (commonMonths.includes(item.monthIndex)) {
				return sum + item.total;
			}
			return sum;
		}, 0);

		if (previousYearTotal === 0) return null;
		return ((currentYearTotal - previousYearTotal) / previousYearTotal) * 100;
	};

	const percentageChange = calculatePercentageChange();
	// Flag to determine if we should show the tendencies block
	const showTendencies = percentageChange !== null;

	if (currentYearConsumptionData?.data?.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card className="w-full h-full">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
						<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
						<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
						<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		console.error("Error fetching consumption data:", error);
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!currentYearData.length) {
		return (
			<Card className="w-full max-w-full overflow-hidden border-gray-200 dark:border-gray-800 shadow-sm">
				<CardContent className="p-4">
					<div className="text-center text-gray-500">
						Aucune donnée disponible
					</div>
				</CardContent>
			</Card>
		);
	}

	// Calculate Y-axis range
	const maxTotal = Math.max(...currentYearData.map((d) => d.total));
	const roundedMax = Math.ceil((maxTotal + 2) / 4) * 4;
	const yAxisTicks = [0, roundedMax / 2, roundedMax];

	const CustomTooltip = ({
		active,
		payload,
		label,
	}: TooltipProps<number, string>) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 text-xs max-w-[165px]">
					<p className="font-semibold text-xs mb-1">{`${label}: ${formatCurrency(
						payload[0].payload.total,
					)}`}</p>
					<div className="space-y-1">
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-teal-400" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Heures Creuses
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.offPeak)}
							</span>
						</div>
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-pink-500" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Heures Pleines
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.peak)}
							</span>
						</div>
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1">
								<div className="w-2 h-2 rounded-sm bg-[#0F172A]" />
								<span className="text-gray-600 dark:text-gray-300 text-xs">
									Abonnement
								</span>
							</div>
							<span className="font-medium text-xs">
								{formatCurrency(payload[0].payload.subscription)}
							</span>
						</div>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<Card className="w-full max-w-full overflow-hidden border-gray-200 dark:border-gray-800 shadow-sm">
			<CardHeader className="pb-1 pt-3 px-3 sm:px-4">
				<div className="flex justify-between items-center">
					<div>
						<CardTitle className="text-base sm:text-lg font-bold">
							Dépenses mensuelles
						</CardTitle>
						<CardDescription className="text-xs mt-0.5">
							{currentDate.getFullYear()}
						</CardDescription>
					</div>
				</div>
			</CardHeader>

			<CardContent className="p-2 sm:p-3">
				<div className="h-52 sm:h-64 md:h-72 w-full">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={currentYearData}
							margin={{ top: 24, right: 10, left: 0, bottom: 5 }}
							barGap={4}
							barCategoryGap={12}
						>
							<XAxis
								dataKey="month"
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 10, fontWeight: 500 }}
								dy={5}
							/>
							<YAxis
								axisLine={false}
								tickLine={true}
								tick={{ fontSize: 10, fill: "#666" }}
								tickFormatter={(value) => value}
								width={34}
								domain={[0, roundedMax]}
								ticks={yAxisTicks}
							/>
							<YAxis
								yAxisId="grid"
								orientation="right"
								tickLine={false}
								axisLine={false}
								tick={false}
								width={0}
							/>
							<Tooltip
								content={<CustomTooltip />}
								cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
							/>

							<Bar
								dataKey="subscription"
								stackId="a"
								fill="#0F172A"
								animationDuration={1200}
								label="Abonnement"
							/>
							<Bar
								dataKey="peak"
								stackId="a"
								fill="rgba(236, 72, 153, 0.85)"
								animationDuration={1200}
							/>
							<Bar
								dataKey="offPeak"
								stackId="a"
								fill="rgba(45, 212, 191, 0.85)"
								radius={[4, 4, 0, 0]}
								animationDuration={1200}
							>
								<LabelList
									dataKey="total"
									position="top"
									formatter={(value: number) => formatCurrency(value)}
									className="text-xs fill-gray-700 dark:fill-gray-300"
								/>
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Legend */}
				<div className="flex justify-center mt-2 gap-4">
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-teal-400 mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Heures Creuses
						</span>
					</div>
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-pink-500 mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Heures Pleines
						</span>
					</div>
					<div className="flex items-center">
						<div className="w-2.5 h-2.5 rounded-sm bg-[#0F172A] mr-1.5" />
						<span className="text-xs text-gray-600 dark:text-gray-300">
							Abonnement
						</span>
					</div>
				</div>

				{/* Trending - Only displayed if we have comparable data */}
				{showTendencies && (
					<div className="mt-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-center gap-1 text-xs font-medium text-gray-800 dark:text-gray-200">
							{percentageChange > 0 ? (
								<TrendingUp className="h-3.5 w-3.5 text-red-500" />
							) : (
								<TrendingDown className="h-3.5 w-3.5 text-green-500" />
							)}
							{percentageChange > 0 ? (
								<span className="text-red-500">
									Vos dépenses ont augmenté de{" "}
									{Math.abs(percentageChange).toFixed(1)}% par rapport à la même
									période l'année dernière
								</span>
							) : (
								<span className="text-green-500">
									Vos dépenses ont diminué de{" "}
									{Math.abs(percentageChange).toFixed(1)}% par rapport à la même
									période l'année dernière
								</span>
							)}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

export default AnnualExpenses;
